import { Phone, Clock, Shield, Star } from "lucide-react";
import { Button } from "./ui/button";

export const Hero = () => {
  return (
    <section className="relative min-h-[80vh] md:min-h-[75vh] lg:min-h-[80vh] flex items-center bg-zinc-950 overflow-hidden">
      {/* Background Image - MOBILE VERSION */}
      <div className="absolute inset-0 md:hidden">
        <img
          src="/serrurier-lieusaint-depannage-urgence-ouverture-porte-24h.jpg"
          alt="Serrurier Lieusaint dépannage urgence ouverture de porte 24h/24 intervention rapide"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
      </div>

      {/* Background Image - DESKTOP VERSION */}
      <div className="absolute inset-0 hidden md:block">
        <img
          src="/serrurier-lieusaint-depannage-urgence-ouverture-porte-24h-24.jpg"
          alt="Serrurier Lieusaint dépannage urgence ouverture de porte 24h/24 intervention rapide"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 py-12 md:py-20">
        <div className="max-w-3xl">
          {/* Google Reviews Badge - positioned at top */}
          <div className="flex flex-wrap items-center gap-3 mb-5 md:mb-6 animate-fade-in">
            {/* Availability Badge */}
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/50 backdrop-blur-sm rounded-sm px-3 py-2">
              <Clock className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium uppercase tracking-wide">
                24h/24 – 7j/7
              </span>
            </div>
            
            {/* Google Rating Badge with inline SVG logo */}
            <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-sm px-3 py-2 shadow-sm">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="text-zinc-900 font-bold text-sm">4,9</span>
                <span className="text-zinc-500 text-sm">/5</span>
              </div>
              {/* Google "G" logo - official colors */}
              <svg className="w-5 h-5 ml-1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
          </div>

          {/* H1 Title */}
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-white mb-4 md:mb-6 animate-slide-up font-heading drop-shadow-lg"
            data-testid="hero-title"
          >
            Serrurier Lieusaint
            <span className="block text-yellow-400 text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2 drop-shadow-md">
              Dépannage 24h/24
            </span>
          </h1>

          {/* Subtitle - Desktop */}
          <p className="hidden md:block text-lg lg:text-xl text-white/90 mb-6 md:mb-8 max-w-2xl animate-slide-up stagger-1 drop-shadow-md">
            <strong className="text-yellow-400">Ouverture de porte</strong> – <strong className="text-yellow-400">Changement de serrure</strong> – <strong className="text-yellow-400">Intervention rapide</strong>
          </p>
          
          {/* Subtitle - Mobile */}
          <p className="md:hidden text-base text-white/90 mb-5 max-w-md animate-slide-up stagger-1 drop-shadow-md">
            <strong className="text-yellow-400">Intervention rapide</strong> – <strong className="text-yellow-400">Ouverture de porte</strong> – <strong className="text-yellow-400">Serrure</strong>
          </p>

          {/* Trust badges - Desktop only */}
          <div className="hidden md:flex flex-wrap gap-4 md:gap-6 mb-8 md:mb-10 animate-slide-up stagger-2">
            <div className="flex items-center gap-2 text-white/90 bg-black/30 backdrop-blur-sm rounded-sm px-3 py-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              <span className="text-sm">Devis avant intervention</span>
            </div>
            <div className="flex items-center gap-2 text-white/90 bg-black/30 backdrop-blur-sm rounded-sm px-3 py-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-sm">Intervention 15-30 min</span>
            </div>
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden md:flex flex-col sm:flex-row gap-4 animate-slide-up stagger-3">
            <a href="tel:0668032561" data-testid="hero-call-button">
              <Button className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-sm shadow-[0_0_40px_rgba(250,204,21,0.5)] animate-pulse-glow hover:shadow-[0_0_50px_rgba(250,204,21,0.7)] transition-all">
                <Phone className="w-5 h-5 mr-3" />
                APPELER MAINTENANT – 06 68 03 25 61
              </Button>
            </a>
            <a href="#services">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-black font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-sm transition-all backdrop-blur-sm bg-white/10"
                data-testid="hero-services-button"
              >
                Nos services
              </Button>
            </a>
          </div>

          {/* CTA Button - Mobile */}
          <div className="md:hidden flex flex-col gap-4 animate-slide-up stagger-3">
            <a href="tel:0668032561" data-testid="hero-call-button-mobile">
              <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-6 py-5 text-base rounded-sm shadow-[0_0_40px_rgba(250,204,21,0.5)] animate-pulse-glow">
                <Phone className="w-5 h-5 mr-2" />
                APPELER – 06 68 03 25 61
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Decorative gradient at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

export default Hero;
